/**
 * WHAT: Lateral movement (mover/leaver) report tracking enterprise user changes across database technologies.
 * WHY:  When employees change departments, managers, job titles, or leave the organization, their
 *       database access may need adjustment; this report surfaces those identity changes so compliance
 *       teams can trigger access reviews and prevent stale or inappropriate access from persisting.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_CONFIG from '../config/api.config';
import './LateralMovementReport.css';

/**
 * WHAT: Renders an overview of enterprise user changes with time-period and change-type filters and detail views.
 * WHY:  Provides a single view for compliance teams to monitor organizational changes (manager, department,
 *       job title, account status) that may indicate access risk, with drilldowns into individual changes
 *       and disabled-manager exceptions.
 */
const LateralMovementReport = () => {
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [changes, setChanges] = useState([]);
  const [disabledManagerExceptions, setDisabledManagerExceptions] = useState([]);

  // Filters
  const [selectedChangeType, setSelectedChangeType] = useState('ALL');
  const [timePeriod, setTimePeriod] = useState(30); // days
  const [activeView, setActiveView] = useState('overview'); // overview, changes, departments, manager, department, jobtitle, disabled, enabled, exceptions
  const [expandedAccordion, setExpandedAccordion] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || API_CONFIG.getEndpoint('/api/databases');

  useEffect(() => {
    fetchData();
  }, [timePeriod, selectedChangeType]);

  /* WHAT: Fetches statistics, change history, and disabled-manager exceptions from the API.
   * WHY:  Consolidates three related API calls into one refresh cycle so the UI stays consistent
   *       when time-period or change-type filters change. */
  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch statistics
      const statsResponse = await axios.get(API_CONFIG.getEnterpriseUsersEndpoint('changes/statistics/'), {
        params: { days: timePeriod }
      });
      setStatistics(statsResponse.data);

      // Fetch change history with filters
      const params = { days: timePeriod, limit: 100 };
      if (selectedChangeType !== 'ALL') {
        params.change_type = selectedChangeType;
      }

      const changesResponse = await axios.get(API_CONFIG.getEnterpriseUsersEndpoint('changes/'), { params });
      setChanges(changesResponse.data.changes || []);

      // Fetch disabled manager exceptions
      const exceptionsResponse = await axios.get(API_CONFIG.getEnterpriseUsersEndpoint('exceptions/disabled-managers/'));
      setDisabledManagerExceptions(exceptionsResponse.data.exceptions || []);

    } catch (error) {
      console.error('Error fetching mover-leaver data:', error);
    } finally {
      setLoading(false);
    }
  };

  /* WHAT: Returns the Bootstrap badge CSS class for a given change type.
   * WHY:  Each change type has a distinct color to help compliance users visually scan tables. */
  const getChangeTypeBadgeClass = (changeType) => {
    switch (changeType) {
      case 'MANAGER_CHANGE':
        return 'badge bg-warning text-dark';
      case 'DEPARTMENT_CHANGE':
        return 'badge bg-info';
      case 'JOB_TITLE_CHANGE':
        return 'badge bg-secondary';
      case 'ACCOUNT_DISABLED':
        return 'badge bg-dark';
      case 'ACCOUNT_ENABLED':
        return 'badge bg-success';
      default:
        return 'badge bg-secondary';
    }
  };

  /* WHAT: Formats an ISO date string into a human-readable US locale format.
   * WHY:  Provides consistent date presentation across the report's tables. */
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /* WHAT: Looks up a disabled-manager exception record for a given employee.
   * WHY:  Employees whose manager is disabled are compliance risks; this lookup enables inline warning badges. */
  const getDisabledManagerException = (employeeId) => {
    return disabledManagerExceptions.find(
      exc => exc.employee_id === employeeId || exc.user_id === employeeId
    );
  };

  /* WHAT: Renders a red "Disabled Manager" badge next to an employee if their manager is disabled.
   * WHY:  Visually flags employees who may have unreviewed access because their manager can no longer attest. */
  const renderDisabledManagerWarning = (employeeId) => {
    const exception = getDisabledManagerException(employeeId);
    if (!exception) return null;

    return (
      <span
        className="badge bg-danger ms-2"
        data-bs-toggle="tooltip"
        data-bs-placement="top"
        title={exception.exception_message}
        style={{ cursor: 'help' }}
      >
        <i className="fas fa-exclamation-triangle me-1"></i>
        Disabled Manager
      </span>
    );
  };

  /* WHAT: Sets the active change-type filter and switches from overview to changes view.
   * WHY:  Clicking a change-type badge in the overview acts as a drill-down shortcut. */
  const handleChangeTypeClick = (changeType) => {
    setSelectedChangeType(changeType);
    // Switch to changes view when a change type is selected
    if (activeView === 'overview' || activeView === 'exceptions') {
      setActiveView('changes');
    }
  };

  /* WHAT: Renders the overview tab with change-type distribution cards and a preview of recent changes.
   * WHY:  Gives compliance users an at-a-glance summary before drilling into detailed change lists. */
  const renderOverview = () => {
    if (!statistics) return null;

    // Get top 10 recent changes for preview
    const recentChanges = changes.slice(0, 10);

    return (
      <div>
        {/* Change Type Distribution */}
        <div className="row mb-4">
          <div className="col-md-12">
            <div className="card shadow-sm border-0">
              <div className="card-header bg-white py-3">
                <h5 className="mb-0">Change Type Distribution</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-3 mb-3">
                    <div className="d-flex align-items-center mb-1">
                      <span className="fw-semibold me-2">Total Changes</span>
                      <button
                        className="badge bg-primary text-white border-0"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleChangeTypeClick('ALL')}
                      >
                        {statistics.total_changes}
                      </button>
                    </div>
                  </div>
                  <div className="col-md-3 mb-3">
                    <div className="d-flex align-items-center mb-1">
                      <span className="fw-semibold me-2">Manager Changes</span>
                      <button
                        className="badge bg-warning text-dark border-0"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleChangeTypeClick('MANAGER_CHANGE')}
                      >
                        {statistics.manager_changes}
                      </button>
                    </div>
                  </div>
                  <div className="col-md-3 mb-3">
                    <div className="d-flex align-items-center mb-1">
                      <span className="fw-semibold me-2">Department Changes</span>
                      <button
                        className="badge bg-info text-white border-0"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleChangeTypeClick('DEPARTMENT_CHANGE')}
                      >
                        {statistics.department_changes}
                      </button>
                    </div>
                  </div>
                  <div className="col-md-3 mb-3">
                    <div className="d-flex align-items-center mb-1">
                      <span className="fw-semibold me-2">Job Title Changes</span>
                      <button
                        className="badge bg-secondary text-white border-0"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleChangeTypeClick('JOB_TITLE_CHANGE')}
                      >
                        {statistics.job_title_changes}
                      </button>
                    </div>
                  </div>
                  <div className="col-md-3 mb-3">
                    <div className="d-flex align-items-center mb-1">
                      <span className="fw-semibold me-2">Account Disabled</span>
                      <button
                        className="badge bg-dark text-white border-0"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleChangeTypeClick('ACCOUNT_DISABLED')}
                      >
                        {statistics.account_disabled}
                      </button>
                    </div>
                  </div>
                  <div className="col-md-3 mb-3">
                    <div className="d-flex align-items-center mb-1">
                      <span className="fw-semibold me-2">Account Enabled</span>
                      <button
                        className="badge bg-success text-white border-0"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleChangeTypeClick('ACCOUNT_ENABLED')}
                      >
                        {statistics.account_enabled}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Changes Preview */}
        <div className="row mb-4">
          <div className="col-md-12">
            <div className="card shadow-sm border-0">
              <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Recent Changes (Last 10)</h5>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => setActiveView('changes')}
                >
                  View All <i className="fas fa-arrow-right ms-1"></i>
                </button>
              </div>
              <div className="card-body">
                {recentChanges.length === 0 ? (
                  <p className="text-muted text-center mb-0">No recent changes found</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Employee</th>
                          <th>Change Type</th>
                          <th>Change Summary</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentChanges.map((change) => (
                          <tr key={change.change_id}>
                            <td>
                              <div>
                                <strong>{change.display_name}</strong>
                                {renderDisabledManagerWarning(change.employee_id || change.user_id)}
                                <br />
                                <small className="text-muted">{change.user_principal_name}</small>
                              </div>
                            </td>
                            <td>
                              <span className={getChangeTypeBadgeClass(change.change_type)}>
                                {change.change_type_display}
                              </span>
                            </td>
                            <td>
                              <small>
                                <strong className="text-primary">{change.change_summary}</strong>
                              </small>
                            </td>
                            <td>
                              <small>{formatDate(change.detected_date)}</small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* WHAT: Toggles an accordion section open or closed by index.
   * WHY:  Only one employee's change details should be expanded at a time to keep the list readable. */
  const toggleAccordion = (index) => {
    setExpandedAccordion(expandedAccordion === index ? null : index);
  };

  /* WHAT: Renders the full changes list grouped by employee in an accordion layout.
   * WHY:  Grouping by employee lets reviewers see all of one person's changes together,
   *       which is critical for spotting patterns (e.g., department + manager change = transfer). */
  const renderAllChanges = () => {
    // Group changes by employee_id
    const groupedChanges = changes.reduce((acc, change) => {
      const key = change.employee_id || change.user_id;
      if (!acc[key]) {
        acc[key] = {
          employee_id: change.employee_id,
          display_name: change.display_name,
          user_principal_name: change.user_principal_name,
          changes: []
        };
      }
      acc[key].changes.push(change);
      return acc;
    }, {});

    const groupedArray = Object.values(groupedChanges);

    // Get title based on selected filter
    const getChangeTypeTitle = () => {
      switch (selectedChangeType) {
        case 'MANAGER_CHANGE':
          return 'Manager Changes';
        case 'DEPARTMENT_CHANGE':
          return 'Department Changes';
        case 'JOB_TITLE_CHANGE':
          return 'Job Title Changes';
        case 'ACCOUNT_DISABLED':
          return 'Account Disabled';
        case 'ACCOUNT_ENABLED':
          return 'Account Enabled';
        default:
          return 'All Changes';
      }
    };

    return (
      <div className="card shadow-sm border-0">
        <div className="card-header bg-white py-3">
          <h5 className="mb-0">{getChangeTypeTitle()} ({changes.length} changes from {groupedArray.length} users)</h5>
        </div>
        <div className="card-body">
          {changes.length === 0 ? (
            <p className="text-muted text-center mb-0">No changes match the selected filters</p>
          ) : (
            <div className="accordion" id="changesAccordion">
              {groupedArray.map((group, index) => (
                <div className="accordion-item border mb-2" key={group.employee_id || index}>
                  <h2 className="accordion-header">
                    <button
                      className={`accordion-button ${expandedAccordion === index ? '' : 'collapsed'}`}
                      type="button"
                      onClick={() => toggleAccordion(index)}
                      aria-expanded={expandedAccordion === index}
                      aria-controls={`collapse${index}`}
                    >
                      <div className="d-flex justify-content-between align-items-center w-100 me-3">
                        <div>
                          {group.employee_id && (
                            <span className="badge bg-secondary me-2">{group.employee_id}</span>
                          )}
                          <strong>{group.display_name}</strong>
                          {renderDisabledManagerWarning(group.employee_id)}
                          <br />
                          <small className="text-muted">{group.user_principal_name}</small>
                        </div>
                        <span className="badge bg-primary rounded-pill">{group.changes.length} changes</span>
                      </div>
                    </button>
                  </h2>
                  <div
                    id={`collapse${index}`}
                    className={`accordion-collapse collapse ${expandedAccordion === index ? 'show' : ''}`}
                  >
                    <div className="accordion-body p-0">
                      <div className="table-responsive">
                        <table className="table table-sm mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Change Type</th>
                              <th>Change Summary</th>
                              <th>Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.changes.map((change) => (
                              <tr key={change.change_id}>
                                <td>
                                  <span className={getChangeTypeBadgeClass(change.change_type)}>
                                    {change.change_type_display}
                                  </span>
                                </td>
                                <td>
                                  <small>
                                    <strong className="text-primary">{change.change_summary}</strong>
                                  </small>
                                </td>
                                <td>
                                  <small>{formatDate(change.detected_date)}</small>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  /* WHAT: Renders the exceptions tab listing active employees whose managers are disabled.
   * WHY:  Disabled-manager exceptions represent a compliance gap where no one can attest for
   *       an employee's access; surfacing them prompts reassignment. */
  const renderExceptions = () => {
    return (
      <div className="card shadow-sm border-0">
        <div className="card-header bg-danger text-white py-3">
          <h5 className="mb-0">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Disabled Manager Exceptions ({disabledManagerExceptions.length})
          </h5>
          <small>Active employees whose manager is disabled but haven't been reassigned</small>
        </div>
        <div className="card-body">
          {disabledManagerExceptions.length === 0 ? (
            <div className="alert alert-success mb-0">
              <i className="fas fa-check-circle me-2"></i>
              No exceptions found. All active employees have active managers.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Employee ID</th>
                    <th>Employee Name</th>
                    <th>Email</th>
                    <th>Department</th>
                    <th>Job Title</th>
                    <th>Manager ID</th>
                    <th>Manager Name</th>
                    <th>Exception</th>
                  </tr>
                </thead>
                <tbody>
                  {disabledManagerExceptions.map((exception, index) => (
                    <tr key={index}>
                      <td>
                        <span className="badge bg-secondary">{exception.employee_id}</span>
                      </td>
                      <td>
                        <strong>{exception.display_name}</strong>
                      </td>
                      <td>
                        <small className="text-muted">{exception.user_principal_name}</small>
                      </td>
                      <td>{exception.department || 'N/A'}</td>
                      <td>{exception.job_title || 'N/A'}</td>
                      <td>
                        <span className="badge bg-secondary">{exception.manager_id}</span>
                      </td>
                      <td>
                        {exception.manager_name ? (
                          <>
                            {exception.manager_name}
                            <br />
                            <small className="text-muted">{exception.manager_user_principal_name}</small>
                          </>
                        ) : (
                          <span className="text-danger">Manager not found</span>
                        )}
                      </td>
                      <td>
                        <span
                          className="badge bg-danger"
                          data-bs-toggle="tooltip"
                          title={exception.exception_message}
                        >
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          {exception.exception_type === 'DISABLED_MANAGER' ? 'Disabled Manager' : 'Manager Not Found'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container-fluid mt-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading mover-leaver data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-md-12">
          <h2>
            <i className="fas fa-exchange-alt me-2"></i>
            Mover-Leaver Report
          </h2>
          <p className="text-muted">
            Monitor organizational changes, promotions, demotions, lateral movements, and account status changes
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="row mb-4">
        <div className="col-md-12">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Time Period</label>
                  <select
                    className="form-select"
                    value={timePeriod}
                    onChange={(e) => setTimePeriod(parseInt(e.target.value))}
                  >
                    <option value={7}>Last 7 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={60}>Last 60 days</option>
                    <option value={90}>Last 90 days</option>
                    <option value={180}>Last 6 months</option>
                    <option value={365}>Last year</option>
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold">Change Type</label>
                  <select
                    className="form-select"
                    value={selectedChangeType}
                    onChange={(e) => setSelectedChangeType(e.target.value)}
                  >
                    <option value="ALL">All Change Types</option>
                    <option value="MANAGER_CHANGE">Manager Changes</option>
                    <option value="DEPARTMENT_CHANGE">Department Changes</option>
                    <option value="JOB_TITLE_CHANGE">Job Title Changes</option>
                    <option value="ACCOUNT_DISABLED">Account Disabled</option>
                    <option value="ACCOUNT_ENABLED">Account Enabled</option>
                  </select>
                </div>

                <div className="col-md-4 d-flex align-items-end">
                  <button
                    className="btn btn-primary w-100"
                    onClick={fetchData}
                  >
                    <i className="fas fa-sync-alt me-2"></i>
                    Refresh Data
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* View Tabs */}
      <div className="row mb-4">
        <div className="col-md-12">
          <ul className="nav nav-tabs">
            <li className="nav-item">
              <button
                className={`nav-link ${activeView === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveView('overview')}
              >
                <i className="fas fa-chart-line me-2"></i>
                Overview
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeView === 'changes' ? 'active' : ''}`}
                onClick={() => setActiveView('changes')}
              >
                <i className="fas fa-list me-2"></i>
                Changes
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeView === 'exceptions' ? 'active' : ''}`}
                onClick={() => setActiveView('exceptions')}
              >
                <i className="fas fa-exclamation-triangle me-2"></i>
                Exceptions
                {disabledManagerExceptions.length > 0 && (
                  <span className="badge bg-danger ms-2">{disabledManagerExceptions.length}</span>
                )}
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Content Area */}
      <div className="row">
        <div className="col-md-12">
          {activeView === 'overview' && renderOverview()}
          {activeView === 'changes' && renderAllChanges()}
          {activeView === 'exceptions' && renderExceptions()}
        </div>
      </div>
    </div>
  );
};

export default LateralMovementReport;
